import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../shared/ui/button';
import { FactList, FactRow } from '../../shared/ui/fact-list';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Select } from '../../shared/ui/select';
import { ShortId } from '../../shared/ui/table';
import {
  generalSecrets,
  sourceSecrets,
  testSend,
  updateSource,
  type TSource,
  type TTestSend,
} from '../../entities/source';

const secretLabel = (hint: string, state: string): string =>
  `${hint} — ${state === 'grace' ? 'in overlap' : 'active'}`;

const skew = (ms: number): string => {
  const seconds = ms / 1000;
  if (Math.abs(seconds) < 1) return `${ms}ms`;
  return `${Math.round(seconds).toLocaleString('en-US')}s`;
};

type TRefusal = NonNullable<TTestSend['refusedBy']>;

const REFUSED: Record<TRefusal, { title: string; description: string }> = {
  disabled: {
    title: 'Refused — the source is disabled',
    description:
      'A sender gets 410 Gone and nothing is stored. Senders may switch the webhook off for good on seeing it; enable the source to take requests again.',
  },
  general_address_closed: {
    title: 'Refused — the general address is closed',
    description:
      'A sender posting here gets 404, as for an address that does not exist. Only client addresses take requests on this source.',
  },
  method_not_allowed: {
    title: 'Refused — POST is not accepted',
    description:
      'A sender gets 405. Tick POST under What we accept, then save, if this sender posts.',
  },
  rate_limited: {
    title: 'Refused — over the rate limit',
    description: 'A sender gets 429 and is told when to retry. Nothing was stored.',
  },
  auth: {
    title: 'Signature rejected',
    description:
      'A sender gets 404 — the same answer as an address that does not exist. That is deliberate, so a caller cannot use the reply to work out whether an address is real.',
  },
};

const ACCEPTED: Record<TTestSend['authResult'], { title: string; description: string }> = {
  verified: {
    title: 'Signature verified',
    description: 'The request was accepted and handed on, same as a real one.',
  },
  failed: {
    title: 'Kept, delivered, and marked unverified',
    description:
      'The signature did not match. This source keeps such requests, so it was stored, delivered like any other, and its events are marked unverified.',
  },
  skipped: {
    title: 'Accepted — nothing was checked',
    description:
      'This source checks nothing, so the request was taken as it came. Anyone who knows the address gets the same result.',
  },
};

// The server words it from the same set fanout acts on: the live flows of the source.
const deliveryLine = (result: TTestSend): string =>
  result.deliverySummary ??
  (result.targets.length === 0
    ? 'Nothing will be delivered — no flow takes requests from this source'
    : result.targets.map((target) => target.name).join(', '));

interface ITestSendProps {
  source: TSource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWatch: (requestId: string) => void;
  onFlows: () => void;
}

export const TestSend = ({ source, open, onOpenChange, onWatch, onFlows }: ITestSendProps) => {
  const client = useQueryClient();
  const [eventType, setEventType] = useState('');
  const [secretId, setSecretId] = useState('');
  const [result, setResult] = useState<TTestSend | null>(null);
  const [confirming, setConfirming] = useState(false);

  const secrets = useQuery({
    queryKey: ['source-secrets', source.id],
    queryFn: () => sourceSecrets(source.id),
    enabled: open,
  });
  const usable = generalSecrets(secrets.data ?? []).filter(
    (secret) => secret.state === 'active' || secret.state === 'grace',
  );

  const send = useMutation({
    mutationFn: () =>
      testSend(source.id, {
        ...(eventType.trim() ? { eventType: eventType.trim() } : {}),
        ...(secretId ? { secretId } : {}),
      }),
    onSuccess: setResult,
  });

  const accept = useMutation({
    mutationFn: () => updateSource(source.id, { onAuthFailure: 'accept_and_flag' }),
    onSuccess: () => {
      setConfirming(false);
      void client.invalidateQueries({ queryKey: ['source', source.id] });
    },
  });

  const close = () => {
    onOpenChange(false);
    setResult(null);
    setConfirming(false);
    send.reset();
    accept.reset();
  };

  if (!result) {
    return (
      <Modal
        open={open}
        onOpenChange={(next) => !next && close()}
        title="Send a test request"
        description="We post to your own ingest address, exactly as a sender would, and show you what happened."
        submit={{
          label: 'Send it',
          onSubmit: () => send.mutate(),
          waiting: send.isPending,
          waitingLabel: 'Waiting for the answer',
        }}
      >
        <p className="mb-label text-micro font-medium tracking-group text-faint">What to send</p>
        <Input
          mono
          placeholder="invoice.paid"
          value={eventType}
          onChange={(event) => setEventType(event.target.value)}
        />
        <Hint>
          The sample body is the server's. Leave this empty and the sample's own type is used
        </Hint>

        {usable.length > 0 && (
          <>
            <p className="mt-4 mb-label text-micro font-medium tracking-group text-faint">
              Sign it with
            </p>
            <Select
              aria-label="Sign it with"
              value={secretId}
              onChange={setSecretId}
              options={[
                { value: '', label: 'Whichever is active' },
                ...usable.map((secret) => ({
                  value: secret.id,
                  label: secretLabel(secret.secretHint, secret.state),
                })),
              ]}
            />
          </>
        )}

        {send.isError && (
          <Note tone="danger" className="mt-3">
            The request never left. Nothing was sent.
          </Note>
        )}
      </Modal>
    );
  }

  if (confirming) {
    return (
      <Modal
        open={open}
        onOpenChange={(next) => !next && close()}
        title="Keep requests that fail the check?"
        description={`From now on, every request to ${source.name} whose signature does not match is stored, handed to every flow that takes it, and marked unverified — forged ones from anyone who knows the address included.`}
        submit={{
          label: 'Keep and mark them',
          onSubmit: () => accept.mutate(),
          waiting: accept.isPending,
          waitingLabel: 'Saving',
        }}
      >
        <Note>
          Meant for the days you spend moving a sender to a new secret. Switch back to rejecting
          under When a check fails once it verifies again.
        </Note>
        {accept.isError && (
          <Note tone="danger" className="mt-3">
            That was not saved. The source still rejects requests that fail.
          </Note>
        )}
      </Modal>
    );
  }

  const refusal = result.refusedBy ?? (result.accepted ? null : 'auth');
  const copy = refusal ? REFUSED[refusal] : ACCEPTED[result.authResult];
  const goesNowhere = result.accepted && result.targets.length === 0;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => !next && close()}
      title={copy.title}
      description={copy.description}
      footer={
        <>
          {refusal === 'auth' && source.onAuthFailure === 'reject' && (
            <Button size="sm" variant="ghost" onClick={() => setConfirming(true)}>
              Keep and mark failures instead
            </Button>
          )}
          {result.requestId && (
            <Button size="sm" variant="ghost" onClick={() => onWatch(result.requestId!)}>
              Watch the deliveries
            </Button>
          )}
          <Button size="sm" onClick={close}>
            Done
          </Button>
        </>
      }
    >
      <FactList>
        <FactRow label="Answered" tone={result.accepted ? 'default' : 'danger'} mono>
          {result.refusedBy && result.refusedBy !== 'auth'
            ? String(result.httpStatus)
            : `${result.httpStatus} in ${result.durationMs}ms`}
        </FactRow>

        {refusal === null || refusal === 'auth' ? (
          <>
            <FactRow label={result.matchedSecretHint ? 'Matched secret' : 'Secrets tried'} mono>
              {result.matchedSecretHint
                ? `${result.matchedSecretHint}${result.matchedSecretSlot ? ` · ${result.matchedSecretSlot}` : ''}`
                : result.authResult === 'skipped'
                  ? 'none — this source checks nothing'
                  : result.secretsTried === 0
                    ? 'none — nothing here could be signed'
                    : `${result.secretsTried} tried, none verified it`}
            </FactRow>

            {result.signedWithSecretHint &&
              result.signedWithSecretId !== result.matchedSecretId && (
                <FactRow label="Signed with" mono>
                  {result.signedWithSecretHint}
                </FactRow>
              )}

            <FactRow label={result.accepted ? 'Header checked' : 'Header we read'} mono>
              {result.headerChecked ?? 'none — this source signs nothing'}
            </FactRow>
          </>
        ) : null}

        {result.timestampSent !== null && result.timestampSent !== undefined && (
          <FactRow label="Timestamp in header" mono>
            {result.timestampSent}
          </FactRow>
        )}
        {result.timestampSkewMs !== null && result.timestampSkewMs !== undefined && (
          <FactRow label="Skew from our clock" mono>
            {skew(result.timestampSkewMs)}
          </FactRow>
        )}

        {result.requestId && (
          <FactRow label="Request" mono>
            <ShortId id={result.requestId} />
          </FactRow>
        )}

        {result.accepted && (
          <FactRow label="Delivery" tone={goesNowhere ? 'danger' : 'default'}>
            {deliveryLine(result)}
          </FactRow>
        )}
      </FactList>

      {goesNowhere && (
        <Note tone="danger" className="mt-3">
          The request is kept, and that is all. A flow is what sends it on.{' '}
          <button
            type="button"
            className="text-primary underline"
            onClick={() => {
              close();
              onFlows();
            }}
          >
            Start a flow from it
          </button>
        </Note>
      )}

      <p className="mt-4 mb-label text-micro font-medium tracking-group text-faint">
        What was checked
      </p>
      <FactList ruled={false}>
        {result.detail.map((line) => (
          <p key={line} className="font-mono text-meta text-muted">
            {line}
          </p>
        ))}
      </FactList>

      {refusal === 'auth' && (
        <Note tone="danger" className="mt-4">
          Most often this is the wrong secret for the wrong environment — a sender issues a
          different one per endpoint, and the test one never matches live traffic.
        </Note>
      )}
    </Modal>
  );
};
